import { useAuth } from "@clerk/react";
import { Error as ErrorIcon, MoreVert, Public } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { onError } from "@/components/core/display-error";
import { ErrorDisplay } from "@/components/core/error-display";
import { LinkToStripe } from "@/components/core/link-to-stripe";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link } from "@/components/core/tanstack-link";
import { EmailOauth } from "@/components/email-settings/email-oauth";
import { formatDate } from "@/helpers/intl";
import { linkToOauthFlow } from "@/helpers/oauth-link";
import { useTrovePlan } from "@/hooks/trove-plan";
import {
  AccessStatus,
  type EmailAccountOut,
  getGetEmailAccountsQueryKey,
  PricingStatusStatus,
  useGetAdminOrganizations,
  useGetEmailAccounts,
  useUpdateEmailAccount,
} from "@/models/backend";

export const Route = createFileRoute("/_authenticated/_contained/admin/settings")({
  component: RouteComponent,
});

const BillingBlock = () => {
  const { status } = useTrovePlan();
  const { data: organization, isLoading, error, isError, refetch } = useGetAdminOrganizations();
  if (isLoading || !organization) {
    return <CardLoadingSkeleton />;
  }
  if (isError) {
    <ErrorDisplay message="Failed to fetch billing information" onRetry={refetch} error={error} />;
  }
  if (status === PricingStatusStatus.TRIAL) {
    return (
      <Stack spacing={2} sx={{ alignItems: "start" }}>
        <Typography variant="h4">Billing</Typography>
        <Typography variant="body1">
          You are currently on a free trial until {formatDate(organization?.trial_end)}. You can add
          your payment details in the Portal, or manage your seats on the{" "}
          <Link to="/admin/organizations">organisation page</Link>
        </Typography>
        <LinkToStripe target="portal" label="Go to Portal" />
      </Stack>
    );
  }
  if (status === PricingStatusStatus.OK) {
    return (
      <Stack spacing={2} sx={{ alignItems: "start" }}>
        <Typography variant="h4">Billing</Typography>
        <Typography variant="body1">
          You are currently paying for {organization?.seats} seats. You can manage your payment
          method and invoices in the portal below, or change the number of seats on the{" "}
          <Link to="/admin/organizations">organisation page</Link>.
        </Typography>
        <LinkToStripe target="portal" label="Go to Portal" />
      </Stack>
    );
  }
  if ([PricingStatusStatus.ERROR, PricingStatusStatus.SOFT_ERROR].includes(status)) {
    return (
      <Stack spacing={2} sx={{ alignItems: "start" }}>
        <Typography variant="h4">Billing</Typography>
        <Typography variant="body1">
          You do not have an active subscription. Please go to the portal below and update your
          payment details.
        </Typography>
        <LinkToStripe target="portal" label="Go to Portal" />
      </Stack>
    );
  }
  return (
    <Stack spacing={2} sx={{ alignItems: "start" }}>
      <Typography variant="h4">Billing</Typography>
      <ErrorDisplay title="There was a problem fetching your account details">
        We ran into an issue fetching your account details. Please try again later, or get in touch
        at <MuiLink href="mailto:hi@trove.works">hi@trove.works</MuiLink>
      </ErrorDisplay>
      <LinkToStripe target="portal" label="Go to Portal" />
    </Stack>
  );
};

const MoreButton = ({
  account,
  onToggleGlobal,
  onEdit,
}: {
  account: EmailAccountOut;
  onToggleGlobal: () => void;
  onEdit?: () => void;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };
  return (
    <>
      <IconButton
        size="small"
        ref={menuTriggerRef}
        aria-controls={menuId}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={handleMenuClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={menuTriggerRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            onToggleGlobal();
            setMenuOpen(false);
          }}
        >
          <ListItemText>
            {account.is_global
              ? "Stop copying to new organisations"
              : "Make available to all your organisations"}
          </ListItemText>
        </MenuItem>
        {onEdit && (
          <MenuItem
            onClick={() => {
              onEdit();
              setMenuOpen(false);
            }}
          >
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const EmailBlock = () => {
  const { getToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const queryClient = useQueryClient();
  useEffect(() => {
    getToken().then((token) => setToken(token));
  }, [getToken]);
  const [isShowingAdd, setIsShowingAdd] = useState(false);
  const { data: accounts, error, isError, isLoading, refetch } = useGetEmailAccounts();
  const { mutate: updateEmailAccount } = useUpdateEmailAccount();
  const handleAddEmail = () => {
    setIsShowingAdd(true);
  };

  const onToggleGlobal = useCallback(
    (account: EmailAccountOut) => {
      updateEmailAccount(
        {
          data: { id: account.id, is_global: !account.is_global },
        },
        {
          onSuccess: () => {
            enqueueSnackbar(`Made email ${account.is_global ? "single account" : "global"}`, {
              variant: "success",
            });
            queryClient.invalidateQueries({
              queryKey: getGetEmailAccountsQueryKey(),
            });
          },
          onError: onError({ prefix: "Error updating email account" }),
        },
      );
    },
    [queryClient.invalidateQueries, updateEmailAccount],
  );

  const columns = useMemo<GridColDef<EmailAccountOut>[]>(
    () => [
      {
        headerName: "First name",
        field: "first_name",
        flex: 1,
      },
      {
        headerName: "Last name",
        field: "last_name",
        flex: 1,
      },
      {
        headerName: "Email",
        field: "email",
        flex: 2,
      },
      {
        type: "actions",
        field: "actions",
        align: "right",
        getActions: ({ row }) => [
          ...(row.access_status === AccessStatus.failed
            ? [
                <Tooltip
                  key="access"
                  title="There was an issue accessing this account, click to reconnect"
                >
                  <IconButton
                    disabled={token === null}
                    color="error"
                    onClick={() => linkToOauthFlow({ provider: row.provider, token })}
                  >
                    <ErrorIcon />
                  </IconButton>
                </Tooltip>,
              ]
            : []),
          ...(row.is_global
            ? [
                <Tooltip
                  key="global"
                  title="This account will be automatically available in all of your organisations"
                >
                  <Public />
                </Tooltip>,
              ]
            : []),
          <MoreButton key="more" account={row} onToggleGlobal={() => onToggleGlobal(row)} />,
        ],
      },
    ],
    [onToggleGlobal, token],
  );
  return (
    <Stack sx={{ width: "100%" }} spacing={2}>
      {isError ? (
        <ErrorDisplay
          title="Failed to load email settings"
          error={error}
          onRetry={() => refetch()}
        />
      ) : (
        <DataGrid<EmailAccountOut>
          rows={accounts ?? []}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick={true}
          showToolbar={false}
        />
      )}
      <Button
        onClick={handleAddEmail}
        variant="contained"
        color="primary"
        sx={{ width: "fit-content", alignSelf: "flex-end" }}
      >
        Add new email
      </Button>
      <Dialog open={isShowingAdd} onClose={() => setIsShowingAdd(false)}>
        <DialogTitle>Add new email</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Sending from a company account will increase the number of customers who respond.
            </Typography>
            <EmailOauth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setIsShowingAdd(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

function RouteComponent() {
  return (
    <Stack spacing={4}>
      <Typography variant="h3">Settings</Typography>
      <BillingBlock />
      <Stack spacing={2}>
        <Typography variant="h4">Team emails</Typography>
        <EmailBlock />
      </Stack>
    </Stack>
  );
}
